import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as ExcelJS from 'exceljs';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, shareReplay, Subject, tap } from 'rxjs';
import { BreadcrumbService } from 'src/app/breadcrums/services/breadcrumb.service';
import { CompanyService } from 'src/app/company-Info/services/company-service';
import { CompanySettingServices } from 'src/app/company-Info/services/companysettings-service';
import { UserDataService } from 'src/app/core/_services/user-data/user-data.service';
import { FilterOption } from 'src/app/shared/components/advance-filter/filter-option.model';
import { sharedService } from 'src/app/shared/shared-service.service';
import { defaultValue, exportExcelHeaderColors, reportingStatusEnum } from 'src/app/shared/_enums/enums';
import { ApiService } from '../../../core/_services/api/api.service';
import { ConfirmDialogService } from '../../../core/_services/confirm-dialog/confirm-dialog.service';
import { invitationType } from '../../../shared/_enums/enums';
import { FormGroup } from '@angular/forms';
import { Action, messages } from './../../components/model'
import * as JSZip from 'jszip';
import { saveAs } from 'file-saver';


// Excel "Accounting" number format used for amounts in exports: the $ sign is pinned to the left edge
// of the cell and the amount is right-aligned. Negatives keep a minus sign; zero is shown as "$ -".
// Same layout as Excel's built-in Accounting format ("$" quoted). Use with EXCEL_ACCOUNTING_ALIGNMENT.
export const EXCEL_ACCOUNTING_FORMAT = '_("$"* #,##0.00_);_("$"* -#,##0.00_);_("$"* "-"??_);_(@_)';
// Same as EXCEL_ACCOUNTING_FORMAT, with negative amounts shown in red (e.g. KPI variances)
export const EXCEL_ACCOUNTING_FORMAT_RED_NEGATIVE = '_("$"* #,##0.00_);[Red]_("$"* -#,##0.00_);_("$"* "-"??_);_(@_)';
// General (no forced horizontal) alignment, like a cell Excel formats as Accounting itself; the format
// does the alignment. A forced left/right/center alignment can stop some apps applying the "$" spacing.
export const EXCEL_ACCOUNTING_ALIGNMENT: Readonly<Partial<ExcelJS.Alignment>> = {};
// Bold font for Total rows. Must keep the workbook's default font (Calibri 11): a bold-only font has no
// name/size, so Excel falls back to another font and the "$" no longer lines up with the rows above.
export const EXCEL_TOTAL_ROW_FONT: Readonly<Partial<ExcelJS.Font>> = { name: 'Calibri', family: 2, size: 11, scheme: 'minor', bold: true };

@Injectable({ providedIn: 'root' })
export class CommonService {
  private subjectName = new Subject<any>(); //need to create a subject
  private fullName = new BehaviorSubject<any>(null);
  private topMenuSelectedValue = new Subject<string>();
  private userId = new Subject<string>();
  private companyId = new Subject<string>();
  private menuId = new Subject<string>();
  private roleId = new Subject<string>();
  private isSuperAdmin = new Subject<string>();
  private roleName = new BehaviorSubject<string>('');
  private firstName = new Subject<string>();
  private lastName = new Subject<string>();
  private accessLevel = new Subject<string>();
  private systemParameter = new Subject<any>();
  systemDateFormat: string = '';
  systemDateTimeFormat: string = '';
  private menusObject = new BehaviorSubject<any>(null);
  userIP: any = '';
  userTimeZone: string = '';
  userResponse: any;
  selectedAgency: any;
  selectedCurrency: any = 'USD';
  defaultZeroCurrency: string = this.addCurrencyFormatting('0');
  getLocationApiKey: any;
  private actionsSubject: BehaviorSubject<Action[]> = new BehaviorSubject<Action[]>([])
  agencyCategoryOptions: BehaviorSubject<{ value: number, name: string }[]> = new BehaviorSubject<{ value: number, name: string }[]>([])
  private filterUrlStore = new Map<string, Observable<any[]>>();


  constructor(
    protected http: HttpClient,
    public datePipe: DatePipe,
    private _apiService: ApiService,
    private _userService: UserDataService,
    protected companyService: CompanyService,
    private breadcrumbService: BreadcrumbService,
    private router: Router,
    private companySettingServices: CompanySettingServices,
    private currencyPipe: CurrencyPipe,
    private sharedService: sharedService,
    private confirmDialog: ConfirmDialogService,
    private cookieService: CookieService,
  ) {
    this.companyService.getGoogleMapApiKey().subscribe((res: any) => {
      this.getLocationApiKey = res?.geoLocationApiKey;
      this.loadGoogleMapsApi();
    });
  }

  private companyIdSubject = new BehaviorSubject<string | null>(null);
  public companyId$ = this.companyIdSubject.asObservable();

  private userIdSubject = new BehaviorSubject<string | null>(null);
  public userId$ = this.userIdSubject.asObservable();

  public roleName$ = this.roleName.asObservable();

  private accessLevelSubject = new BehaviorSubject<string | null>(null);
  public accessLevel$ = this.accessLevelSubject.asObservable();
  private statesSubject = new BehaviorSubject<any[] | null>(null);
  private countriesSubject = new BehaviorSubject<any[] | null>(null);
  private taxTypesSubject = new BehaviorSubject<any[] | null>(null);
  private contactTypesSubject = new BehaviorSubject<any[] | null>(null);
  private addressTypesSubject = new BehaviorSubject<any[] | null>(null);
  private socialNetworksSubject = new BehaviorSubject<any[] | null>(null);
  private tierTypesSubject = new BehaviorSubject<any[] | null>(null);
  private currenciesSubject = new BehaviorSubject<any[] | null>(null);

  private dashboardaccessLevelSubject = new BehaviorSubject<string | null>(
    null
  );
  public dashboardaccessLevel$ =
    this.dashboardaccessLevelSubject.asObservable();

  private systemParameterSubject = new BehaviorSubject<string | null>(null);
  public systemParameter$ = this.systemParameterSubject.asObservable();

  private loadGoogleMapsApi(): void {
    if (document.getElementById('google-maps-api')) {
      return; // Script already added
    }
    const script = document.createElement('script');
    script.id = 'google-maps-api';
    script.src = `https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=places&key=${this.getLocationApiKey}`;
    script.defer = true;
    script.async = true;
    document.head.appendChild(script);
  }

  setCompanyId(companyId: string) {
    this.companyIdSubject.next(companyId);
  }
  setUserId(userId: string) {
    this.userIdSubject.next(userId);
  }
  setAccessLevel(accessLevel: string | null) {
    if (accessLevel) {
      localStorage.setItem('accessLevel', accessLevel);
      this.accessLevelSubject.next(accessLevel);
    }
  }
  setDashboardAccessLevel(dashboardAccessLevel: string | null) {
    localStorage.setItem(
      'DashboardAccess',
      JSON.stringify(dashboardAccessLevel)
    );
    this.dashboardaccessLevelSubject.next(dashboardAccessLevel);
  }

  getUserDetails() {
    const returnUrl = this.cookieService.get('returnUrl');
    this.companyService.getSystemParameterValues().subscribe((res: any) => {
      if (res.success) {
        this.setSystemParameter(res['result']);
        this.systemParameter.next(res['result']);
        localStorage.setItem('systemParameter', JSON.stringify(res['result']));
      }
    });
    this._userService.getLoggedInUserDetails();
    this.userResponse = this._userService
      .getUserDetails()
      .subscribe((data: any) => {
        if (data && data?.length > 0) {
          this.userId.next(data[0]?.id);
          this.menuId.next(data[0]?.menu_Id);
          this.roleId.next(data[0]?.role_Id);
          this.isSuperAdmin.next(data[0]?.is_Super_Admin)
          this.companyId.next(data[0]?.co_Id);
          this.roleName.next(data[0]?.role_Name);
          this.firstName.next(data[0]?.firsT_NAME);
          this.lastName.next(data[0]?.last_Name);
          this.accessLevel.next(data[0]?.accesS_LEVEL);
          this.setAccessLevel(data[0]?.accesS_LEVEL);
          this.sendCompanyName(data[0]?.company_Name)
          localStorage.setItem('invitationTypeId', data[0]?.invitation_Type_Id);
          if (data[0]?.accesS_LEVEL == 'CT1' || data[0]?.accesS_LEVEL == 'C') {
            localStorage.setItem('DashboardAccess', 'corporate-dashboard');
          } else {
            localStorage.setItem('DashboardAccess', 't1-dashboard');
          }
          if (data[0]?.co_Id) {
            localStorage.setItem('companyId', data[0]?.co_Id);
          }
          localStorage.setItem('firstName', data[0]?.firsT_NAME);
          localStorage.setItem('lastName', data[0]?.last_Name);
          localStorage.setItem('invitation_id', data[0]?.invitation_Id);
          localStorage.setItem('invitation_ID', data[0]?.invitation_Id);
          if (data[0]?.role_Name) {
            localStorage.setItem('roleName', data[0]?.role_Name);
          }
          localStorage.setItem('email', data[0]?.email);
          localStorage.setItem('userId', data[0]?.id);
          if (data[0]?.company_Name) {
            localStorage.setItem('companyName', data[0]?.company_Name);
          }
          if (data[0]?.accesS_LEVEL) {
            localStorage.setItem('accessLevel', data[0]?.accesS_LEVEL);
          }
          if (data[0]?.is_Super_Admin) localStorage.setItem('isSuperAdmin', data[0]?.is_Super_Admin);

          localStorage.setItem('is_Company_Aggregate', data[0]?.is_Aggregate);
          if (data[0]?.accesS_LEVEL == 'CT1' || data[0]?.accesS_LEVEL == 'C') {
            this.setDashboardAccessLevel('corporate-dashboard');
          } else if (data[0]?.accesS_LEVEL == 'SA') {
            this.setDashboardAccessLevel('superadmin-dashboard');
          } else if (data[0]?.accesS_LEVEL == 'T2') {
            this.setDashboardAccessLevel('t2-dashboard');
          }
          else {
            this.setDashboardAccessLevel('t1-dashboard');
          }
          this.breadcrumbService.setRoleName(data[0]?.role_Name);
          let fullName = {
            firstName: data[0]?.firsT_NAME,
            lastName: data[0]?.last_Name,
            email: data[0]?.email,
            roleName: data[0]?.role_Name,
          };
          this.sendFullName(fullName);
          this.setCompanyId(data[0]?.co_Id);
          this.setUserId(data[0]?.id);
          if (data[0]?.co_Id !== null) {
            this.companyService
              .getCompanyDataById(data[0]?.co_Id)
              .subscribe((res: any) => {
                if (res.success) {
                  localStorage.setItem(
                    'companyInfo',
                    JSON.stringify(res['result'])
                  );
                  this.breadcrumbService.setCompanyInfo(res['result']);
                  if (data[0]?.co_Id) {
                    this.companySettingServices
                      .generalTabInfo(data[0]?.co_Id)
                      .subscribe((res: any) => {
                        if (res.success) {
                          localStorage.setItem(
                            'generalSettings',
                            JSON.stringify(res['result'])
                          );
                          this.selectedCurrency = res[
                            'result'
                          ]?.companySetUp?.find(
                            (setting: any) =>
                              setting.displaY_NAME === 'Currency'
                          );
                          this.selectedCurrency =
                            this.selectedCurrency?.currencY_CODE;
                        }
                      });
                  }
                }
              });
          }
          if (returnUrl) {
            const loggedInCompanyId = localStorage.getItem('companyId'); // Get the companyId from localStorage
            setTimeout(() => {
              // Extract senderCompanyId from the returnUrl using URLSearchParams
              const urlParams = new URLSearchParams(returnUrl.split('?')[1]);
              const companyId = urlParams.get('companyId');

              if (companyId === loggedInCompanyId) {
                // If the senderCompanyId matches the companyId in localStorage, redirect to the return URL
                this.router.navigateByUrl(returnUrl);
              } else {
                // If they don't match, redirect to the dashboard
                this.router.navigate(['/dashboard']);
              }
              // Delete the cookie once you're done with it
              this.cookieService.delete('returnUrl');
            }, 2000);
          }
          else if (this.getInvitationType() == invitationType.Company) {
            this.router.navigate(['/companyInfo/company-info']);
          }
          else if (this.getInvitationType() == invitationType.Event) {
          }
        }
      });
  }

  sendMenus(menusList: any) {
    this.menusObject.next(menusList);
  }

  getMenus() {
    return this.menusObject.asObservable();
  }

  getLoggedinUserCompanyId() {
    return this.companyId.asObservable();
  }

  getAccessLevel(): Observable<string | null> {
    return this.accessLevel$.pipe(
      map((accessLevel: string | null) => {
        if (accessLevel !== null) {
          return accessLevel; // Return the value from BehaviorSubject
        } else {
          const localStorageValue = localStorage.getItem('accessLevel');
          if (localStorageValue) {
            this.setAccessLevel(localStorageValue); // Update the BehaviorSubject with localStorage value
            if (localStorageValue == 'CT1' || localStorageValue == 'C') {
              this.setDashboardAccessLevel('corporate-dashboard');
            } else {
              this.setDashboardAccessLevel('t1-dashboard');
            }
            return localStorageValue;
          } else {
            return null; // Return null or handle it as per your requirement
          }
        }
      })
    );
  }
  getDashboardAccess(): Observable<string | null> {
    return this.dashboardaccessLevel$.pipe(
      map((accessLevel: string | null) => {
        if (accessLevel !== null) {
          return accessLevel; // Return the value from BehaviorSubject
        } else {
          const localStorageValue = localStorage.getItem('DashboardAccess');

          if (localStorageValue) {
            this.setDashboardAccessLevel(localStorageValue); // Update the BehaviorSubject with localStorage value
            return localStorageValue;
          } else {
            return null; // Return null or handle it as per your requirement
          }
        }
      })
    );
  }

  getLoggedinUserId() {
    return this.userId.asObservable();
  }

  getLoggedinUserMenuId() {
    return this.menuId.asObservable();
  }

  getLoggedinUserRoleId() {
    return this.roleId.asObservable();
  }

  getisSuperAdmin() {
    return this.isSuperAdmin.asObservable(); //
  }

  sendTopMenuSelectedValue(topMenuId: string) {
    this.topMenuSelectedValue.next(topMenuId);
  }

  getTopMenuSelectedValue() {
    return this.topMenuSelectedValue.asObservable();
  }

  sendCompanyName(companyName: string) {
    //the component that wants to update something, calls this fn
    this.subjectName.next({ companyName: companyName }); //next() will feed the value in Subject
  }

  getCompanyName(): Observable<any> {
    //the receiver component calls this function
    return this.subjectName.asObservable(); //it returns as an observable to which the receiver funtion will subscribe
  }

  sendFullName(fullName: any) {
    this.fullName.next(fullName);
  }

  getFullName(): Observable<any> {
    return this.fullName.asObservable();
  }

  // public getMenusDataById(id: number | string): Observable<any[]> {
  //   return this._apiService
  //     .get(`Menu/MenuById${id}`)
  //     .pipe(map((response: any) => response));
  // } 

  public getMenusDataById(id: number | string, userId: number): Observable<any[]> {
    return this._apiService
      .get(`Menu/GetMenusForUser/${id}/${userId}`)
      .pipe(map((response: any) => response));
  }

  changeDateFormat(paramDate: Date) {
    if (paramDate) return this.datePipe.transform(paramDate, 'dd-MMM-yyyy');
    else return '';
  }
  changeDateFormatYYMMDD(paramDate: Date) {
    if (paramDate) return this.datePipe.transform(paramDate, 'yyyy-MM-dd');
    else return '';
  }
  changeDateFormatMMMYYY(paramDate: Date) {
    return this.datePipe.transform(paramDate, 'MMM-yyyy');
  }

  changeDateTimeFormat(paramDateTime: Date) {
    return this.datePipe.transform(paramDateTime, 'dd-MMM-yyyy hh:mm a');
  }

  changeDateTimeToFormat(paramDateTime: Date, format: string) {
    if (!paramDateTime)
      return paramDateTime;
    else
      return this.datePipe.transform(paramDateTime, format);
  }

  formatDateUTC(date: string) {
    try {
      if (!date || date?.toLowerCase?.() == defaultValue?.invalidDate?.toLowerCase?.()) return
      const inputDate = new Date(date);
      inputDate.setMinutes(
        inputDate.getMinutes() - inputDate.getTimezoneOffset()
      );
      return inputDate.toISOString();
    } catch (err) {
      console.error(err)
      return
    }
  }

  apiDateFormat(paramDate: Date) {
    if (paramDate) return this.datePipe.transform(paramDate, 'yyyy-MM-dd');
    else return '';
  }


  datePipeFormatDate(date: string) {
    return this.datePipe.transform(date, 'yyyy-MM-ddTHH:mm:ss.SSSZ');
  }

  // currany format
  addCurrencyFormatting(value: string, unSuffix?: boolean): string {
    let companySetUp: any = localStorage.getItem('generalSettings');
    const numericValue = parseFloat(value);

    if (isNaN(numericValue)) {
      return '';
    }

    if (numericValue === 0) {
      return '$  -';
    }

    if (companySetUp) {
      companySetUp = JSON.parse(companySetUp);
      this.selectedCurrency = companySetUp?.companySetUp?.find(
        (setting: any) => setting?.displaY_NAME === 'Currency'
      );
      this.selectedCurrency = this.selectedCurrency?.currencY_CODE;
    }

    const formattedValue =
      this.currencyPipe.transform(
        value,
        this.selectedCurrency,
        'symbol',
        '1.2-2'
      ) ?? '';

    if (unSuffix) {
      // Remove the trailing '.00' if it exists
      return formattedValue?.replace(/\.00$/, '');
    } else {
      return formattedValue;
    }
  }

  // Cuts an amount to 2 decimals without rounding (2982425.168 -> "2982425.16"), so addCurrencyFormatting
  // shows the original digits. The value is first reduced to 15 significant digits (the precision a JS number
  // holds reliably) to drop binary float noise: 158443862.89 is stored as 158443862.88999998..., which would
  // otherwise be cut to .88.
  truncateToTwoDecimals(value: string | number): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    // A plain decimal string ("158443862.890000000000") is cut as text, so no digit is lost to float precision
    const plainDecimal = /^\s*(-?)(\d+)(?:\.(\d*))?\s*$/.exec(String(value));
    if (typeof value === 'string' && plainDecimal) {
      const [, sign, integerDigits, decimalDigits = ''] = plainDecimal;
      return `${sign}${integerDigits}.${decimalDigits.padEnd(2, '0').slice(0, 2)}`;
    }
    const numericValue = Number(value);
    if (isNaN(numericValue) || Math.abs(numericValue) >= 1e21) {
      return String(value);
    }
    const cleanValue = Number(numericValue.toPrecision(15));
    // String() gives the shortest exact digits; very small values come back in exponent form ("1e-7")
    const valueText = String(cleanValue).includes('e') ? cleanValue.toFixed(20) : String(cleanValue);
    const [integerPart, decimalPart = ''] = valueText.split('.');
    return `${integerPart}.${decimalPart.padEnd(2, '0').slice(0, 2)}`;
  }

  // Formats an amount with all of its original decimals (2982425.168 -> "$2,982,425.168"), for hover titles
  // next to the 2-decimal display value. toPrecision(15) strips float noise from calculated values.
  formatOriginalAmount(value: string | number): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const numericValue = Number(value);
    if (isNaN(numericValue)) {
      return '';
    }
    if (numericValue === 0) {
      return this.addCurrencyFormatting('0');
    }
    const cleanValue = Number(numericValue.toPrecision(15));
    const valueText = String(cleanValue);
    const decimals = valueText.includes('e')
      ? 10
      : (valueText.split('.')[1] ?? '').length;
    return (
      this.currencyPipe.transform(
        cleanValue,
        this.getCurrencyCode(),
        'symbol',
        `1.2-${Math.min(Math.max(decimals, 2), 20)}`
      ) ?? ''
    );
  }

  // Original amount (as a number) for an Excel cell, instead of the 2-decimal display value; the cell's number
  // format shows it with 2 decimals. Returns fallback when the value is empty or not a number. Values that
  // String() writes in exponent form (below 0.000001) are written as 0, so later currency parsing stays valid.
  excelOriginalAmount(originalValue: any, fallback: any = ''): any {
    if (originalValue === null || originalValue === undefined || originalValue === '') {
      return fallback;
    }
    const numericValue = Number(originalValue);
    if (isNaN(numericValue) || Math.abs(numericValue) >= 1e21) {
      return fallback;
    }
    return Math.abs(numericValue) < 1e-6 ? 0 : numericValue;
  }

  getCurrencyCode() {
    let companySetUp: any = localStorage.getItem('generalSettings');
    if (companySetUp) {
      companySetUp = JSON.parse(companySetUp);
      this.selectedCurrency = companySetUp?.companySetUp?.find(
        (setting: any) => setting?.displaY_NAME === 'Currency'
      );
      this.selectedCurrency = this.selectedCurrency?.currencY_CODE;
    }
    return this.selectedCurrency;
  }

  addCurrencyFormattingShort(value: number, axis: boolean): string {
    let formattedValue: any;
    const toFixed = axis ? 0 : 1;
    if (Math.abs(value) >= 1.0e9) {
      formattedValue = `${(value / 1.0e9)?.toFixed(toFixed)}`;
    } else if (Math.abs(value) >= 1.0e6) {
      formattedValue = `${(value / 1.0e6)?.toFixed(toFixed)}`;
    } else if (Math.abs(value) >= 1.0e3) {
      formattedValue = `${(value / 1.0e3)?.toFixed(toFixed)}`;
    } else {
      formattedValue = value;
    }
    return `${formattedValue}`;
  }

  addCurrencyFormattingShortChart(value: number, isValue: boolean): string {
    let formattedValue: any;
    const toFixed = 0; // Always show integers for thousands
    if (isValue) {
      formattedValue = `${(value / 1.0e3)?.toFixed(toFixed)}`;
      return `${formattedValue}`;
    } else {
      if (Math.abs(value) >= 1.0e3) {
        formattedValue =
          (value / 1.0e3)?.toLocaleString(undefined, {
            maximumFractionDigits: toFixed,
          }) + 'K';
      } else {
        formattedValue = value;
      }
      const currencyCode = this.getCurrencyCode();
      const currencySymbol = this.getCurrencySymbol(currencyCode);
      return `${currencySymbol}${formattedValue}`;
    }
  }

  getCurrencySymbol(currencyCode: string): string {
    const currencySymbols: { [key: string]: string } = {
      USD: '$',
      EUR: '€',
    };
    return currencySymbols[currencyCode] || '';
  }

  removeCurrencyFormatting(value: string | number): string {
    if (value !== null && value !== undefined) {
      const stringValue = String(value);
      const retValue = stringValue?.replace(/[^\d.-]/g, '');
      if (retValue != '-')
        return retValue;
      else
        return '0'
    } else {
      return '';
    }
  }

  CompareDates(effectiveDateField: Date, expirationDateField: Date) {
    let effectiveDate = effectiveDateField;
    let expirationDate = expirationDateField;
    if (effectiveDate > expirationDate) {
      return true;
    }
    return false;
  }

  getAvialbleCount(avilablelen: any, selectedlen: any, type: string) {
    if (type === 'minus') {
      return avilablelen - selectedlen;
    } else {
      return avilablelen + selectedlen;
    }
  }

  isDateOutOfRange(date: Date, minDate: Date, maxDate: Date): boolean {
    return date < minDate || date > maxDate;
  }

  // To change the date format
  detectBrowserName(): string {
    const agent = window.navigator.userAgent.toLowerCase();
    const browser =
      agent.indexOf('edge') > -1
        ? 'Edge'
        : agent.indexOf('edg') > -1
          ? 'Edge'
          : agent.indexOf('opr') > -1
            ? 'Opera'
            : agent.indexOf('chrome') > -1
              ? 'Chrome'
              : agent.indexOf('trident') > -1
                ? 'Internet Explorer'
                : agent.indexOf('firefox') > -1
                  ? 'Firefox'
                  : agent.indexOf('safari') > -1
                    ? 'Safari'
                    : 'other';

    return browser;
  }

  getPhoneNumber(number: any, isd: any) {
    if (isd != '' && isd !== null && isd !== undefined) {
      if (number !== undefined && number) {
        let phoneNumberValue;
        const phoneUtil =
          require('google-libphonenumber').PhoneNumberUtil.getInstance();
        try {
          const phoneNumber = phoneUtil.parseAndKeepRawInput(number, isd);
          if (phoneUtil.isValidNumber(phoneNumber)) {
            phoneNumberValue =
              '+' + '' + phoneUtil.getCountryCodeForRegion(isd) + '-' + number;
            return phoneNumberValue;
          }
          else {
            return '';
          }
        } catch (error: any) { }
      }
    } else {
      return number;
    }
    return '';
  }

  public getStates(): Observable<any[]> {
    if (this.statesSubject.value) {
      return of(this.statesSubject.value);
    }

    return this._apiService.get(`State`).pipe(
      map((response: any) => response),
      tap((states: any[]) => {
        this.statesSubject.next(states);
      })
    );
  } // FN

  public getCountries(): Observable<any[]> {
    if (this.countriesSubject.value) {
      return of(this.countriesSubject.value);
    }
    return this._apiService.get(`Countries`).pipe(
      map((response: any) => response),
      tap((countries: any[]) => {
        this.countriesSubject.next(countries);
      })
    );
  } // FN

  public getContactTypes(): Observable<any[]> {
    if (this.contactTypesSubject.value) {
      return of(this.contactTypesSubject.value);
    }

    return this._apiService.get(`ContactType`).pipe(
      map((response: any) => response),
      tap((contactTypes: any[]) => {
        this.contactTypesSubject.next(contactTypes);
      })
    );
  }

  public getAllCurrencies(): Observable<any[]> {
    if (this.currenciesSubject.value) {
      return of(this.currenciesSubject.value);
    }
    return this._apiService.get(`Currency`).pipe(
      map((response: any) => response),
      tap((currencies: any[]) => {
        this.currenciesSubject.next(currencies);
      })
    );
  }

  public getAllTierTypes(): Observable<any[]> {
    if (this.tierTypesSubject.value) {
      return of(this.tierTypesSubject.value);
    }

    return this._apiService.get(`TierType`).pipe(
      map((response: any) => response),
      tap((tierTypes: any[]) => {
        this.tierTypesSubject.next(tierTypes);
      })
    );
  }

  public getTaxTypes(): Observable<any[]> {
    if (this.taxTypesSubject.value) {
      return of(this.taxTypesSubject.value);
    }

    return this._apiService.get(`TaxType`).pipe(
      map((response: any) => response),
      tap((taxTypes: any[]) => {
        this.taxTypesSubject.next(taxTypes);
      })
    );
  }

  public getSocialNetworks(): Observable<any[]> {
    if (this.socialNetworksSubject.value) {
      return of(this.socialNetworksSubject.value);
    }

    return this._apiService.get(`SocialNetwork`).pipe(
      map((response: any) => response),
      tap((socialNetworks: any[]) => {
        this.socialNetworksSubject.next(socialNetworks);
      })
    );
  } //

  public getAllNAICS(): Observable<any[]> {
    return this._apiService.get(`Naics`).pipe(map((response: any) => response));
  } //

  public getNAICSDetailsByCode(naicsCode: any): Observable<any[]> {
    return this._apiService
      .get(`Naics/${naicsCode}`)
      .pipe(map((response: any) => response));
  } //

  public getAddressTypes(): Observable<any[]> {
    if (this.addressTypesSubject.value) {
      return of(this.addressTypesSubject.value);
    }
    return this._apiService.get(`AddressType`).pipe(
      map((response: any) => response),
      tap((addressTypes: any[]) => {
        this.addressTypesSubject.next(addressTypes);
      })
    );
  }

  public getDiversityTypes(): Observable<any[]> {
    return this._apiService
      .get(`DiversityTypes`)
      .pipe(map((response: any) => response));
  }

  public getCertifyingAgencies(): Observable<any[]> {
    return this._apiService
      .get(`CertifyingAgencies`)
      .pipe(map((response: any) => response));
  }

  public getEmployeeRanges(): Observable<any[]> {
    return this._apiService
      .get(`EmployeeRanges`)
      .pipe(map((response: any) => response));
  }

  public getAllEthnicities(): Observable<any[]> {
    return this._apiService
      .get(`Ethnicities`)
      .pipe(map((response: any) => response));
  }

  public getYearsByCompanyId(co_Id: any): Observable<any[]> {
    return this._apiService
      .get(`ReportingPeriods/GetYearsByCompany?CompanyId=${co_Id}`)
      .pipe(map((response: any) => response));
  }

  // advance filtering service methods
  public advanceFiltersOptionsByModuleIdApi(
    moduleId: string | number,
    companyId?: string | number
  ): Observable<any[]> {
    let co_id = companyId ? `&co_id=${companyId}` : '';
    return this._apiService
      .get(
        `AdvanceFilter/GetFilterOptionsByModuleID?moduleID=${moduleId}${co_id}`
      )
      .pipe(map((response: any) => response));
  }

  public advanceFiltersFiledsApi(api_url: string): Observable<any[]> {
    if (!this.filterUrlStore.has(api_url)) {
      this.filterUrlStore.set(
        api_url,
        this._apiService.get(api_url).pipe(
          map((response: any) => response),
          shareReplay(1) // Caches the last emitted value
        )
      );
    }

    return this.filterUrlStore.get(api_url)!;
  }

  public processFilterResponse(filterResponse: any[]): Observable<any[]> {
    const requests: any = [];
    // Create a map to associate the API requests with their corresponding filter objects
    const requestMap: { [key: string]: Observable<any> } = {};

    // Iterate through the filterResponse array
    filterResponse.forEach((filter: any) => {
      const apiUrl = filter.display_Field_Api_Url;

      // Check if apiUrl exists and field_Type_Name is 'Dropdown List'
      if (
        (apiUrl && filter.field_Type_Name === 'Dropdown List') ||
        filter.field_Type_Name === 'Multi Select'
      ) {
        filter.options = [];
        // Make an API request for each URL and store the Observable in the requestMap
        const request = this.advanceFiltersFiledsApi(apiUrl);
        requestMap[filter.advanceFilter_Id] = request;
      }
    });

    return forkJoin(requestMap).pipe(
      map((responses: any) => {
        // Iterate through the filterResponse array and update options based on the responses
        filterResponse.forEach((filter: any) => {
          const advanceFilterId = filter.advanceFilter_Id;
          if (advanceFilterId in responses) {
            const response = responses[advanceFilterId];
            if (response && response.result) {
              filter.options = response.result
                .map((option: any, index: number) => ({
                  title: option.title
                    ? option.title
                    : option.certifyinG_AGENCY_NAME,
                  value: option.value ? option.value : `${option.abbreviation}`,
                  id: option.value ? option.value : `${option.abbreviation}`,
                }))
                .filter((option: any, index: number, self: any) => {
                  const isDuplicate = self.some(
                    (t: any, idx: any) =>
                      (t.value === option.value || t.id === option.id) &&
                      idx < index
                  );
                  return !isDuplicate;
                });
            }
          }
        });

        return filterResponse;
      })
    );
  }

  public processFilterResponseWithQueryParams(filterResponse: any[], queryParams: any = {}): Observable<any[]> {
    const requests: any = [];
    // Create a map to associate the API requests with their corresponding filter objects
    const requestMap: { [key: string]: Observable<any> } = {};

    // Iterate through the filterResponse array
    filterResponse.forEach((filter: any) => {
      var apiUrl = filter.display_Field_Api_Url;

      if (apiUrl?.indexOf("{") > -1) {
        ///When url contains query params,
        ///replace params with their key - value pair in the url

        let paramsStartIndexes: number[] = this.getAllIndexes(apiUrl, "{");
        let paramsEndIndexes = this.getAllIndexes(apiUrl, "}");
        let startIndex, endIndex, paramKey, paramValue;
        let paramKeys = [];

        if (paramsStartIndexes.length > 0 && paramsEndIndexes.length > 0) {
          for (let i = 0; i < paramsStartIndexes.length; i++) {
            startIndex = paramsStartIndexes[i];
            endIndex = paramsEndIndexes[i];
            paramKey = apiUrl.substring(startIndex + 1, endIndex);
            paramKeys.push(paramKey);
          }

          for (let i = 0; i < paramKeys.length; i++) {
            paramKey = paramKeys[i];

            if (queryParams[paramKey]) {
              paramValue = queryParams[paramKey];
              apiUrl = apiUrl.replace("{" + paramKey + "}", `${paramKey}=${paramValue}`);
            }
            else {
              paramKey = "{" + paramKey + "}";
              apiUrl = apiUrl.replace("&" + paramKey, "").replace(paramKey, "");
            }
          }
        }
      }

      // Check if apiUrl exists and field_Type_Name is 'Dropdown List'
      if (
        (apiUrl && filter.field_Type_Name === 'Dropdown List') ||
        filter.field_Type_Name === 'Multi Select'
      ) {
        filter.options = [];
        // Make an API request for each URL and store the Observable in the requestMap
        const request = this.advanceFiltersFiledsApi(apiUrl);
        requestMap[filter.advanceFilter_Id] = request;
      }
    });

    return forkJoin(requestMap).pipe(
      map((responses: any) => {
        // Iterate through the filterResponse array and update options based on the responses
        filterResponse.forEach((filter: any) => {
          const advanceFilterId = filter.advanceFilter_Id;
          if (advanceFilterId in responses) {
            const response = responses[advanceFilterId];
            if (response && response.result) {
              filter.options = response.result
                .map((option: any, index: number) => ({
                  title: option.title
                    ? option.title
                    : option.certifyinG_AGENCY_NAME,
                  value: option.value ? option.value : `${option.abbreviation}`,
                  id: option.value ? option.value : `${option.abbreviation}`,
                }))
                .filter((option: any, index: number, self: any) => {
                  const isDuplicate = self.some(
                    (t: any, idx: any) =>
                      (t.value === option.value || t.id === option.id) &&
                      idx < index
                  );
                  return !isDuplicate;
                });
            }
          }
        });

        return filterResponse;
      })
    );
  }

  public savedAdvancedFilterList(payload: any): Observable<any[]> {
    return this._apiService
      .get(
        `AdvanceFilter/GetSavedAdvanceFiltersByUser?User_Id=${payload.userId}&Module_Id=${payload.moduleId}&Co_Id=${payload.companyId}`
      )
      .pipe(map((response: any) => response));
  }

  public saveAdvanceFilter(data: any): Observable<any> {
    return this._apiService.post(
      `AdvanceFilter/SaveAdvanceFiltersByUser`,
      data
    );
  }

  public getAdvanceFilterOPtions(payload: any, url: string): Observable<any> {
    return this._apiService.post(url, payload);
  }

  // get sass toka
  public getSassToken(type: any): Observable<any> {
    return this._apiService.get(`SASToken/${type}`);
  }

  public updateSharedPublicAdvanceFilter(
    payload: any,
    Is_Shared_Public: boolean
  ): Observable<any> {
    return this._apiService.put(
      `AdvanceFilter/UpdateAdvanceFilterAsShared?Is_Shared_Public=${Is_Shared_Public}`,
      payload
    );
  }

  public deleteSavedAdvanceFilter(payload: any): Observable<any> {
    return this._apiService.post(
      `AdvanceFilter/DeleteAdvanceFilterByUser`,
      payload
    );
  }

  public getSavedFilterDetail(data: any): Observable<any> {
    return this._apiService.get(
      `AdvanceFilter/GetAdvanceFilterDetailsByFilterID?User_Filter_Id=${data.User_Filter_Id}`
    );
  }

  public getAdvanceFilterSearchedDetails(payload: any): Observable<any> {
    // when user click saved advance filter details then call this api for rendar saved filter.
    return this._apiService.post(
      `AdvanceFilter/GetAdvanceFilterDetailsByFilterID`,
      payload
    );
  }

  public getAllDEI = (): Observable<any[]> => {
    return this._apiService
      .get(`AdvanceFilter/GetDEIForSelection`)
      .pipe(map((response: any) => response));
  };
  public getAgenciesForSelectionOfDiversityTypes = (
    diversityTypes: any
  ): Observable<any[]> => {
    let diversity = '';

    if (diversityTypes?.length > 0) {
      diversity = `?DiversityTypes=${diversityTypes}`;
    }

    return this._apiService
      .get(`AdvanceFilter/GetAgenciesForSelection${diversity}`)
      .pipe(map((response: any) => response));
  };

  // certifcate number validation

  getSelectedAgenciesForNumberValidation(agency: any) {
    this.selectedAgency = agency;
  }
  getValidationTestcase() {
    let validate = {
      min: '',
      max: '50',
      pattern: '^[a-zA-Z0-9\\-\\/]{1,15}$',  // Added -/
    };

    if (this.selectedAgency?.abbreviation === 'CAMSC') {
      validate = {
        min: '4',
        max: '9',
        pattern: '^(?!0+$)[A-Za-z0-9\\-\\/]*$'
      };
      return validate;
    } else if (this.selectedAgency?.abbreviation === 'NGLCC') {
      validate = {
        min: '5',
        max: '7',
        pattern: '^(?!0+$)[A-Za-z0-9\\-\\/]*$'
      };
      return validate;
    } else if (this.selectedAgency?.abbreviation === 'NVBDC') {
      validate = {
        min: '5',
        max: '15',
        pattern: '^(?!0+$)[a-zA-Z0-9\\-\\/]*$',  // Added -/
      };
      return validate;
    } else if (this.selectedAgency?.abbreviation === 'WEBC') {
      validate = {
        min: '4',
        max: '10',
        pattern: '^(?!0+$)(CA)?[0-9\\-\\/]*$',  // Added -/
      };
      return validate;
    } else if (this.selectedAgency?.abbreviation === 'CGLCC') {
      validate = {
        min: '11',
        max: '11',
        pattern: '^[a-zA-Z]{5}(?!000000)[0-9]{6}$',
      };
      return validate;
    } else if (this.selectedAgency?.abbreviation === 'STATE') {
      validate = {
        min: '2',
        max: '15',
        pattern: '^(?!0+$)[A-Z0-9\\-/]{2,15}$',
      };
      return validate;
    } else if (this.selectedAgency?.abbreviation === 'SBA-VET') {
      validate = {
        min: '4',
        max: '6',
        pattern: '^(?!0+$)[A-Z0-9\\-/]{4,6}$',
      };
      return validate;
    } else if (this.selectedAgency?.abbreviation === 'WBEC Metro NY') {
      validate = {
        min: '5',
        max: '10',
        pattern: '^(?!0+$)[A-Z0-9\\-/]{5,10}$',
      };
      return validate;
    } else if (
      this.selectedAgency?.abbreviation === 'WBEC Greater DMV' ||
      this.selectedAgency?.abbreviation === 'WBEC East' ||
      this.selectedAgency?.abbreviation === 'WBEC Florida' ||
      this.selectedAgency?.abbreviation === 'WBEC ORV' ||
      this.selectedAgency?.abbreviation === 'WBEC South' ||
      this.selectedAgency?.abbreviation === 'WBEC-Pacific' ||
      this.selectedAgency?.abbreviation === 'WBEC-West'
    ) {
      validate = {
        min: '4',
        max: '10',
        pattern: '^(?!0+$)[A-Z0-9\\-/]{4,10}$'
      };
      return validate;
    } else if (
      this.selectedAgency?.abbreviation === 'CRMSDC' ||
      this.selectedAgency?.abbreviation === 'CVMSDC' ||
      this.selectedAgency?.abbreviation === 'ChicagoMSDC' ||
      this.selectedAgency?.abbreviation === 'DFWMSDC' ||
      this.selectedAgency?.abbreviation === 'EMSDC' ||
      this.selectedAgency?.abbreviation === 'FSMSDC' ||
      this.selectedAgency?.abbreviation === 'GMSDC' ||
      this.selectedAgency?.abbreviation === 'GNEMSDC' ||
      this.selectedAgency?.abbreviation === 'HMSDC' ||
      this.selectedAgency?.abbreviation === 'MMSDC' ||
      this.selectedAgency?.abbreviation === 'MidStatesMSDC' ||
      this.selectedAgency?.abbreviation === 'MPMSDC' ||
      this.selectedAgency?.abbreviation === 'NMSDC-US' ||
      this.selectedAgency?.abbreviation === 'NYNJMSDC' ||
      this.selectedAgency?.abbreviation === 'NorthCentralMSDC' ||
      this.selectedAgency?.abbreviation === 'NWMMSDC' ||
      this.selectedAgency?.abbreviation === 'OhioMSDC' ||
      this.selectedAgency?.abbreviation === 'PSWMSDC' ||
      this.selectedAgency?.abbreviation === 'PRMSDC' ||
      this.selectedAgency?.abbreviation === 'SCMSDC' ||
      this.selectedAgency?.abbreviation === 'SRMSDC' ||
      this.selectedAgency?.abbreviation === 'SMSDC' ||
      this.selectedAgency?.abbreviation === 'TSMSDC' ||
      this.selectedAgency?.abbreviation === 'WRMSDC' ||
      this.selectedAgency?.abbreviation === 'DIN'
    ) {
      validate = {
        min: '6',
        max: '10',
        pattern: '^((CR|CA|CH|DL|PT|FL|AT|GN|HS|MI|IN|MP|US|NY|WS|NW|CN|AZ|PR|SC|SR|AU|TN|WR)?[a-zA-Z0-9 \\-]{6,10}|CR|CA|CH|DL|PT|FL|AT|GN|HS|MI|IN|MP|US|NY|WS|NW|CN|AZ|PR|SC|SR|AU|TN|WR[a-zA-Z0-9 \\-]{0,8})$'
      };
      return validate;
    } else {
      return validate;
    }
  }

  getCertificateNumberMatch(value: string): string | null {
    const certificationTypes: any = {
      CAMSC: /^(?!0+$)[0-9]{4,9}$/,
      NGLCC: /^(?!0+$)[0-9]{5,7}$/,
      NVBDC: /^(?!0+$)[a-zA-Z0-9]{5,15}$/,
      'NMSDC-US':
        /^(?!0+$)(0(US[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|US[0-9]{5})$/,
      CRMSDC: /^(?!0+$)(0(CR[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|CR[0-9]{5})$/, //^(?!0+$)(0(CR[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|CR[a-zA-Z0-9]{5,})$/,
      CVMSDC: /^(?!0+$)(0(CA[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|CA[0-9]{5})$/,
      ChicagoMSDC:
        /^(?!0+$)(0(CH[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|CH[0-9]{5})$/,
      DFWMSDC: /^(?!0+$)(0(DL[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|DL[0-9]{5})$/,
      EMSDC: /^(?!0+$)(0(PT[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|PT[0-9]{5})$/,
      FSMSDC: /^(?!0+$)(0(FL[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|FL[0-9]{5})$/,
      GMSDC: /^(?!0+$)(0(AT[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|AT[0-9]{5})$/,
      GNEMSDC: /^(?!0+$)(0(GN[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|GN[0-9]{5})$/,
      HMSDC: /^(?!0+$)(0(HS[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|HS[0-9]{5})$/,
      MMSDC: /^(?!0+$)(0(MI[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|MI[0-9]{5})$/,
      NYNJMSDC: /^(?!0+$)(0(NY[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|NY[0-9]{5})$/,
      NorthCentralMSDC:
        /^(?!0+$)(0(WS[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|WS[0-9]{5})$/,
      NWMMSDC: /^(?!0+$)(0(NW[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|NW[0-9]{5})$/,
      OhioMSDC: /^(?!0+$)(0(CN[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|CN[0-9]{5})$/,
      PSWMSDC: /^(?!0+$)(0(AZ[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|AZ[0-9]{5})$/,
      PRMSDC: /^(?!0+$)(0(PR[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|PR[0-9]{5})$/,
      SCMSDC: /^(?!0+$)(0(SC[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|SC[0-9]{5})$/,
      SRMSDC: /^(?!0+$)(0(SR[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|SR[0-9]{5})$/,
      SMSDC: /^(?!0+$)(0(AU[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|AU[0-9]{5})$/,
      TSMSDC: /^(?!0+$)(0(TN[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|TN[0-9]{5})$/,
      WRMSDC: /^(?!0+$)(0(WR[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|WR[0-9]{5})$/,
      DIN: /^(?!0+$)(0(WR[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|WR[0-9]{5})$/,
      MidStatesMSDC:
        /^(?!0+$)(0(IN[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|IN[0-9]{5})$/,
      MPMSDC: /^(?!0+$)(0(MP[a-zA-Z0-9]{5,}|[a-zA-Z0-9]{6,10})|MP[0-9]{5})$/,
      CGLCC: /^[a-zA-Z]{5}(?!000000)[0-9]{6}$/,
      STATE: /^(?!0+$)[a-zA-Z0-9]{2,15}$/,
      SBA: /^(?!0+$)[a-zA-Z0-9]{4,6}$/,

      'WBEC Metro NY': /^(?!0+$)[a-zA-Z0-9]{5,10}$/,
      'WBEC Greater DMV': /^(?!0+$)[a-zA-Z0-9]{4,10}$/,
      'WBEC East': /^(?!0+$)[a-zA-Z0-9]{4,10}$/,
      'WBEC Florida': /^(?!0+$)[a-zA-Z0-9]{4,10}$/,
      'WBEC ORV': /^(?!0+$)[a-zA-Z0-9]{4,10}$/,
      'WBEC South': /^(?!0+$)[a-zA-Z0-9]{4,10}$/,
      'WBEC-Pacific': /^(?!0+$)[a-zA-Z0-9]{4,10}$/,
      'WBEC-West': /^(?!0+$)[a-zA-Z0-9]{4,10}$/,

      // Add more certification types as needed
    };

    if (this.selectedAgency?.abbreviation) {
      const isAllZeros = /^0+$/.test(value);
      const isValidFormat =
        certificationTypes[this.selectedAgency?.abbreviation]?.test(value);
      if (isAllZeros) {
        return "Number should not be all 0's.";
      }
      let characterRange = 'Invalid format.';
      if (this.selectedAgency?.abbreviation === 'CAMSC') {
        characterRange = 'Invalid format. Please enter 4-9 numeric characters.';
      } else if (this.selectedAgency?.abbreviation === 'NGLCC') {
        characterRange = 'Invalid format. Please enter 5-7 numeric characters.';
      } else if (this.selectedAgency?.abbreviation === 'NVBDC') {
        characterRange = 'Invalid format.Please enter 5-15 characters.';
      } else if (this.selectedAgency?.abbreviation === 'WEBC') {
        characterRange = 'Invalid format.Please enter 4-10 characters.';
      } else if (this.selectedAgency?.abbreviation === 'CGLCC') {
        characterRange =
          'Invalid format.Please enter a 5-letter prefix followed by 6 numeric characters.';
      } else if (this.selectedAgency?.abbreviation === 'STATE') {
        characterRange = 'Invalid format. Please enter 2-15 characters.';
      } else if (this.selectedAgency?.abbreviation === 'SBA') {
        characterRange = 'Invalid format.Please enter 4-6 characters.';
      } else if (this.selectedAgency?.abbreviation === 'WBEC Metro NY') {
        characterRange = 'Invalid format.Please enter 5-10 characters.';
      } else if (
        this.selectedAgency?.abbreviation === 'WBEC Greater DMV' ||
        this.selectedAgency?.abbreviation === 'WBEC East' ||
        this.selectedAgency?.abbreviation === 'WBEC Florida' ||
        this.selectedAgency?.abbreviation === 'WBEC ORV' ||
        this.selectedAgency?.abbreviation === 'WBEC South' ||
        this.selectedAgency?.abbreviation === 'WBEC-Pacific' ||
        this.selectedAgency?.abbreviation === 'WBEC-West'
      ) {
        characterRange = 'Invalid format.Please enter 4-10 characters.';
      } else if (
        this.selectedAgency?.abbreviation === 'CRMSDC' ||
        this.selectedAgency?.abbreviation === 'CVMSDC' ||
        this.selectedAgency?.abbreviation === 'ChicagoMSDC' ||
        this.selectedAgency?.abbreviation === 'DFWMSDC' ||
        this.selectedAgency?.abbreviation === 'EMSDC' ||
        this.selectedAgency?.abbreviation === 'FSMSDC' ||
        this.selectedAgency?.abbreviation === 'GMSDC' ||
        this.selectedAgency?.abbreviation === 'GNEMSDC' ||
        this.selectedAgency?.abbreviation === 'HMSDC' ||
        this.selectedAgency?.abbreviation === 'MMSDC' ||
        this.selectedAgency?.abbreviation === 'MidStatesMSDC' ||
        this.selectedAgency?.abbreviation === 'MPMSDC' ||
        this.selectedAgency?.abbreviation === 'NMSDC-US' ||
        this.selectedAgency?.abbreviation === 'NYNJMSDC' ||
        this.selectedAgency?.abbreviation === 'NorthCentralMSDC' ||
        this.selectedAgency?.abbreviation === 'NWMMSDC' ||
        this.selectedAgency?.abbreviation === 'OhioMSDC' ||
        this.selectedAgency?.abbreviation === 'PSWMSDC' ||
        this.selectedAgency?.abbreviation === 'PRMSDC' ||
        this.selectedAgency?.abbreviation === 'SCMSDC' ||
        this.selectedAgency?.abbreviation === 'SRMSDC' ||
        this.selectedAgency?.abbreviation === 'SMSDC' ||
        this.selectedAgency?.abbreviation === 'TSMSDC' ||
        this.selectedAgency?.abbreviation === 'WRMSDC' ||
        this.selectedAgency?.abbreviation === 'DIN'
      ) {
        characterRange = 'Invalid format.Please enter 6-10 characters.';
      }
      return isValidFormat != undefined ? isValidFormat ? null : characterRange : null;
    }
    return null;
  }

  findActiveReportingPeriod(data: any) {
    let openReport = data?.find(
      (item: any) =>
        item?.status_Key?.toLowerCase?.() !==
        reportingStatusEnum.YetToOpen.toLowerCase?.()
    );

    openReport = this.formateDateTimeActiveReportingPeriod(openReport);

    if (openReport) {
      return openReport;
    } else {
      // If "Reporting Open" is not found, find the report with reporting end date near today
      const nearestReport = this.findClosestRecord(data);
      return nearestReport;
    }
  }

  findClosestRecord(data: any) {
    const today = new Date();
    let nearestReport = data?.reduce((closest: any, current: any) => {
      const currentDate = today.getTime();
      const currentReportEndDate = new Date(
        current.reporting_End_Date
      ).getTime();
      const closestReportEndDate = new Date(
        closest.reporting_End_Date
      ).getTime();

      const timeDiffCurrent = Math.abs(currentReportEndDate - currentDate);
      const timeDiffClosest = Math.abs(closestReportEndDate - currentDate);

      return timeDiffCurrent < timeDiffClosest ? current : closest;
    });

    nearestReport = this.formateDateTimeActiveReportingPeriod(nearestReport);
    return nearestReport;
  }

  formateDateTimeActiveReportingPeriod(report: any) {
    if (report) {
      report.period_Start_Date = this.changeDateFormat(
        report?.period_Start_Date
      )!;
      report.period_End_Date = this.changeDateFormat(report?.period_End_Date)!;
      report.reporting_Start_Date = this.changeDateFormat(
        report.reporting_Start_Date
      )!;
      report.reporting_End_Date = this.changeDateFormat(
        report.reporting_End_Date
      )!;
      report.reporting_End_Date = this.changeDateFormat(
        report.reporting_End_Date
      )!;
      report.name = `${report.reporting_Period_Name} - ${report.reporting_Period_Year}`;
      return report;
    } else {
      return null;
    }
  }

  convertCurrencyValue(value: any) {
    if (value >= 1000000) {
      // Format as millions with one decimal place
      return `$${(value / 1000000).toFixed(0)}M`;
    } else if (value >= 1000) {
      // Format as thousands
      return `$${(value / 1000).toFixed(0)}K`;
    } else {
      // Display as is (less than 1000)
      return `$${value}`;
    }
  }

  corporateDashboardChartApi(co_id: number, rptId?: any): Observable<any> {
    const url = rptId
      ? `DashBoard/getReportingPeriodDashBoard?co_id=${co_id}&rpt_Period_Id=${rptId}`
      : `DashBoard/getReportingPeriodDashBoard?co_id=${co_id}`;
    return this._apiService.get(url).pipe(map((response: any) => response));
  }

  getActionPathNames(accessType: any): Observable<any> {
    return this._apiService
      .get(`Menu/GetAllActionsPath?accessType=${accessType}`)
      .pipe(map((response: any) => response));
  }

  //Before navigating to another screen checking details saved or not
  public canExit(
    componentForm: any,
    saveBtnClickFlag: boolean
  ): Promise<boolean> {
    if (!saveBtnClickFlag) {
      if (componentForm) {
        return this.sharedService
          .openRouteConfirmation()
          .then((result: boolean) => {
            if (result) {
              return true; // Allow navigation
            } else {
              //reloading to get back Active Menus
              //location.reload();
              return false; // Block navigation
            }
          });
      } else {
        return Promise.resolve(true);
      }
    } else {
      return Promise.resolve(true);
    }
  }

  //To check conflicts for reporting period
  isReportingPeriodConflict(
    reportingList: any,
    newPeriodStartDate: any,
    newPeriodEndDate: any,
    newReportingStartDate: any,
    newReportingEndDate: any,
    newReportingPeriodName?: any,
    newReportingPeriodYear?: any,
    isUpdate?: boolean,
    reportingPeriodIdToUpdate?: any,
    isReportingCheck?: boolean,
  ) {
    const errorList = [];
    const newPeriod = {
      periodStart: new Date(newPeriodStartDate),
      periodEnd: new Date(newPeriodEndDate),
      reportingStart: new Date(newReportingStartDate),
      reportingEnd: new Date(newReportingEndDate),
    };

    const isNameConflicted = reportingList?.some((existingData: any) => {
      if (isUpdate && existingData.id === reportingPeriodIdToUpdate) {
        return false;
      }
      const normalizedNewName = newReportingPeriodName?.toLowerCase?.();
      const normalizedExistingName =
        existingData.reporting_Period_Name?.toLowerCase?.();
      const isNewYearEqual =
        existingData.reporting_Period_Year === newReportingPeriodYear;
      return normalizedNewName === normalizedExistingName && isNewYearEqual;
    });

    if (isNameConflicted) {
      errorList.push('\nReporting period with the same name already exists.\n');
    }
    const isConflicted = reportingList?.some((existingData: any) => {
      if (isUpdate && existingData.id === reportingPeriodIdToUpdate) {
        return false;
      }
      const existingPeriodStartDate = new Date(existingData.period_Start_Date);
      const existingPeriodEndDate = new Date(existingData.period_End_Date);
      const existingReportingStartDate = new Date(
        existingData.reporting_Start_Date
      );
      const existingReportingEndDate = new Date(
        existingData.reporting_End_Date
      );
      const periodConflict =
        newPeriod.periodStart <= existingPeriodEndDate &&
        newPeriod.periodEnd >= existingPeriodStartDate &&
        newReportingPeriodYear == existingData.reporting_Period_Year;
      const reportingDatesConflict = isReportingCheck ?
        (!(newPeriod.reportingStart >= existingReportingStartDate) ||
          !(newPeriod.reportingEnd >= existingReportingEndDate)) &&
        !isUpdate : false;
      if (periodConflict) {
        errorList.push('\nConflict(s) identified in period dates.\n');
      } else if (reportingDatesConflict) {
        errorList.push('\nConflict(s) identified in reporting dates.\n');
      }

      if (periodConflict || reportingDatesConflict) {
        return true;
      }
      return false;
    });

    return errorList;
  }

  getQuickLinks(id: number, companyId: number): Observable<any> {
    return this._apiService
      .get(`QuickLinks/GetQuickLinks?user_id=${id}&co_id=${companyId}`)
      .pipe(map((response: any) => response));
  }

  public saveQuickLinks(data: any): Observable<any> {
    return this._apiService.post(`QuickLinks/AddQuickLink`, data);
  }

  public updateQuickLinks(data: any): Observable<any> {
    return this._apiService.put(`QuickLinks/UpdateQuickLink`, data);
  }

  public deleteQuickLinks(id: any): Observable<any> {
    return this._apiService.post(`QuickLinks/DeleteQuickLink?id=${id}`, id);
  }

  // Legal Identifier return max length

  getValidationLegalIdentifier(text: number) {
    let validate = {
      min: '',
      max: '20',
      validation: 'noSpecialChars',
    };

    if (text === 1) {
      validate = {
        min: '',
        max: '10',
        validation: 'Federal_Tax_ID',
      };
      return validate;
    } else if (text === 7) {
      validate = {
        min: '',
        max: '15',
        validation: 'noSpecialChars',
      };
      return validate;
    } else {
      return validate;
    }
  }
  setSystemParameter(systemParameter: any) {
    this.systemParameterSubject.next(systemParameter);
  }

  getSystemParameter(): Observable<any | null> {
    return this.systemParameter$.pipe(
      map((systemParameter: any | null) => {
        if (systemParameter !== null) {
          return systemParameter; // Return the value from BehaviorSubject
        } else {
          const localStorageValue = localStorage.getItem('systemParameter');
          if (localStorageValue) {
            this.setSystemParameter(JSON.parse(localStorageValue)); // Update the BehaviorSubject with localStorage value
            return JSON.parse(localStorageValue);
          } else {
            return null; // Return null or handle it as per your requirement
          }
        }
      })
    );
  }

  // advance filter to set or get Start Date and End Date
  checkStartEndDateSelected(filterData: FilterOption[]): boolean {
    const startDateFilter = this.getStartDateData(filterData);
    const endDateFilter = this.getEndDateData(filterData);

    return !!(
      startDateFilter &&
      endDateFilter &&
      ((startDateFilter?.value && !endDateFilter?.value) ||
        (!startDateFilter?.value && endDateFilter?.value))
    );
  }

  getStartDateData(data: FilterOption[]) {
    return data?.find((option) => option.field_Type_Name === 'Start Date');
  }
  getEndDateData(data: FilterOption[]) {
    return data?.find((option) => option.field_Type_Name === 'End Date');
  }

  // dataTable
  passwordValidation(value: any, element: any) {
    if (!value || value.length == 0) {
      return `${element} is required`;
    } else {
      if (value.length! < 6 || value.length > 15) {
        return 'Password must be between 6 and 15 characters';
      }
      return '';
    }
  }
  urlValidation(value: any, element: any, maxLength: number): string {
    let urlPattern =
      /^(https?:\/\/)(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[a-zA-Z0-9-._~:/?#[\]@!$&'()*+,;=]*)?$/;
    if (!value || value.length === 0) {
      return `${element} is required`;
    } else if (typeof value === 'string') {
      if (value.length > Number(maxLength)) {
        return `${element} is less than ${maxLength} characters`;
      }
      if (!urlPattern.test(value)) {
        return 'Please enter a valid URL starting with http:// or https://';
      }
      return '';
    } else {
      return '';
    }
  }
  inputValidation(value: any, element: any, maxLength: number): string {
    if (!value || value.length === 0) {
      return `${element} is required`;
    } else if (typeof value === 'string') {
      if (value.length > Number(maxLength)) {
        return `${element} is less then of ${maxLength} characters`;
      }
      return '';
    } else {
      return '';
    }
  }
  isInvalidPassword(password: any, dataKey: any) {
    // Check if the password is a string and the validation fails
    let data = this.passwordValidation(password, dataKey);
    if (typeof data === 'string' && data.length > 0) {
      return true;
    }
    return false; // Return false if password is not a string
  }
  isInValidUrl(url: string, dataKey: any, dataType: any, maxLength: any) {
    // Check if the password is a string and the validation fails
    if (dataType == 'url') {
      let data = this.urlValidation(url, dataKey, Number(maxLength));
      if (typeof data === 'string' && data.length > 0) {
        return true;
      }
    }
    return false; // Return false if password is not a string
  }
  isInValidInput(value: any, dataKey: any, dataType: any, maxLength: any) {
    // Check if the password is a string and the validation fails
    if (dataType !== 'url') {
      let data = this.inputValidation(value, dataKey, Number(maxLength));
      if (typeof data === 'string' && data.length > 0) {
        return true;
      }
    }
    return false; // Return false if password is not a string
  }
  getAccessLevelFromLocalStorage(): string {
    return localStorage.getItem('accessLevel') ?? '';
  }
  getDashboardAccessFromLocalStorage(): any {
    return localStorage.getItem('DashboardAccess');
  }

  getInvitationType(): any {
    return localStorage.getItem('invitationTypeId');
  }

  getBaseUrl(): string {
    const baseUrl = document.baseURI; // Retrieve the current base URL
    const index = baseUrl.indexOf('/', baseUrl.indexOf('//') + 2); // Find the index of the first "/" after the protocol
    return baseUrl.substring(0, index + 1); // Extract the base URL
  }

  // check tax id is existed or not in  company Information
  public validateTaxId(
    taxType: string | number,
    taxNumber: string | number
  ): Observable<any> {
    return this._apiService.get(
      `Company/validateCompanybyTaxID?tax_Type_Id=${taxType}&tax_Id=${taxNumber}`
    );
  }

  // create support tickets
  public createSupportTicket(data: any): Observable<any> {
    //return this._apiService.post(`Email/EmailSendToOTRS`, data);
    return this._apiService.post(`Email/SendEmailToOTRS`, data);
  }

  public logOut(UserEmail: any): Observable<any> {
    return this._apiService.get(`SessionService/LogOut${UserEmail}`);
  }

  // genrate dynamic color for chart
  generateDynamicColors(numColors: number, defaultColor?: any): string[] {
    let colorPalette = [];
    if (defaultColor?.length > 0) {
      colorPalette = defaultColor;
    } else {
      colorPalette = [
        '#8B4513', // Saddle Brown
        '#CD853F', // Peru
        '#FFB6C1', // Light Pink
        '#4682B4', // Steel Blue
        '#00CED1', // Dark Turquoise
        '#FF5733', // Dark orange
        '#8C1C62', // Dark pink
        '#7B6000', // Dark yellow
        '#3D195B', // Dark violet
        '#006D5B', // Dark teal
      ];
    }

    const colors = [];
    for (let i = 0; i < numColors; i++) {
      colors.push(colorPalette[i % colorPalette.length]); // Loop through the palette
    }
    return colors;
  }

  calculateWidth(dataKey: string, data: any[]) {
    const characterWidth = 6 // Width per character (adjust as needed)
    const contentLength = this.calculateContentLength(dataKey, data);
    const calculatedWidth = (contentLength * characterWidth);
    return calculatedWidth < 60 ? 60 : calculatedWidth; // Return width as string with 'px' suffix
  }

  calculateContentLength(dataKey: string, data: any[]): number {
    if (data && data?.length > 0) {
      const maxLength = Math.max(...data.map(item => item[dataKey]?.length || 0));
      return maxLength;
    }
    return 0;
  }

  calculateWidthHeader(content: string, columnName: string, array: any[]) {
    const minWidth = 24;
    if (array?.length > 0) {
      const characterWidth = 6;
      let calculatedWidthContent = 0 + (content?.length * characterWidth);
      const columnWidth = this.calculateWidth(columnName, array)
      if (columnWidth == 0) calculatedWidthContent = calculatedWidthContent + minWidth
      if (calculatedWidthContent >= columnWidth) {
        return (calculatedWidthContent + minWidth) < 60 ? 60 : calculatedWidthContent + this.getMinWidth(calculatedWidthContent);
      }
      else {
        return columnWidth < 60 ? 60 : columnWidth + this.getMinWidth(columnWidth);
      }
    } else {
      const characterWidth = 6;
      const calculatedWidthContent = (content?.length * characterWidth);
      return (minWidth + calculatedWidthContent) < 60 ? 60 : this.getMinWidth(calculatedWidthContent) + calculatedWidthContent
    }
  }

  getMinWidth(value: any): number {
    return value >= 120 ? 28 : value >= 90 ? 32 : 36;
  }


  createReportExportExcelName(selectedYear: number, selectedPeriodName: string, excelName: string, companyName: string, filename: string = '') {
    return companyName + '_' + excelName + '_' + selectedYear + '-' + selectedPeriodName + filename  + '.xlsx';
  }

  generateReportName(reportName: string, selectedPeriodName: string, selectedYear: string) {
    if (selectedPeriodName == 'All' && selectedYear?.length > 0) {
      return reportName;
      //return reportName + ' - ' + selectedYear;
    }
    else if (selectedPeriodName == '' && selectedYear == '0') {
      return reportName;
    }
    else {
      return reportName + ' - ' + selectedPeriodName;
      //return reportName + ' - ' + selectedPeriodName + '-' + selectedYear;
    }
  }

  isAmountField(value: any) {
    if (
      typeof value === 'number' ||
      /^-?\$?[0-9,.]+$/.test(value) ||   // allows optional minus before digits
      /^\$\s*-$/.test(value) ||
      /^-\$\d+(\.\d{1,2})?$/.test(value) ||
      /^-\$\d{1,3}(,\d{3})*(\.\d{1,2})?$/.test(value)
    ) {
      return true;
    } else {
      return false;
    }
  }


  private async getBase64ImageFromUrl(imageUrl: string): Promise<string> {

    const response = await fetch(imageUrl);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  async generateHeaderforReportsExcel(worksheet: ExcelJS.Worksheet, selectedYear: number, selectedPeriodName: string, reportName: string, goalType: string = '') {

    const imageUrl = 'https://apiuistore.blob.core.windows.net/blob-anonymous/DivTrax2.0/DiversityLogoPNG.png'; // Update this with your image path or URL

    // Convert the image to base64
    const imageBase64 = await this.getBase64ImageFromUrl(imageUrl);

    // Add image to the worksheet
    const imageId = worksheet.workbook.addImage({
      base64: imageBase64.split(',')[1],
      extension: 'png',
    });

    worksheet.mergeCells('A1:A2');
    // worksheet.addImage(imageId, 'A1:A2');
    worksheet.addImage(imageId, {
      tl: { col: 0, row: 0 },
      ext: { width: 150, height: 42 } // Adjust size as needed
    });


    // Add the first header row with the Corporate name
    const corporateRow = worksheet.addRow(['Report                            : ' + reportName]);
    corporateRow.eachCell((cell: any) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'left' };
    });

    // Add the first header row with the author name
    const authorRow = worksheet.addRow(['Year                                : ' + selectedYear]);
    authorRow.eachCell((cell: any) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'left' };
    });

    // Add the second header row with the generated date
    const dateRow = worksheet.addRow(['Report Request           : ' + selectedPeriodName]);
    dateRow.eachCell((cell: any) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'left' };
    })

    if (goalType.toLowerCase() != '') {
      const goaltypeText = goalType.toLowerCase() == 'pos' ? 'Percentage of Sales' : 'Pecentage of Purchase';
      const goalTypeRow = worksheet.addRow(['Goal Type                     : ' + goaltypeText]);
      goalTypeRow.eachCell((cell: any) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'left' };
      })
    }

    // Add an empty row for spacing
    worksheet.addRow([]);

  }


  async exportToExcel(
    data: any,
    worksheet: ExcelJS.Worksheet,
    headers: any
  ) {
    // Get unique keys
    var HeaderColor: string = exportExcelHeaderColors.IntialHeader;
    var HeaderForeColor: string = exportExcelHeaderColors.IntialHeaderForeColor;
    var InitialColumnSize: string = exportExcelHeaderColors.InitialColumnSize;
    var minimumExcelCellWidth: number = Number(exportExcelHeaderColors.minimumExcelCellWidth);

    if (headers.length == 9) {
      worksheet.mergeCells('B7:I7');
    }
    else if (headers.length == 8) {
      worksheet.mergeCells('B7:H7');
    }
    else if (headers.length == 7) {
      worksheet.mergeCells('B7:G7');
    }
    else if (headers.length == 6) {
      worksheet.mergeCells('B7:F7');
    }
    else if (headers.length == 5) {
      worksheet.mergeCells('B7:E7');
    }
    else if (headers.length == 4) {
      worksheet.mergeCells('B7:D7');
    }
    else if (headers.length == 3) {
      worksheet.mergeCells('B7:C7');
    }


    const headerRow1 = worksheet.getCell('A7');
    headerRow1.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: HeaderColor },
    };

    const headerRow2 = worksheet.getCell('B7');
    headerRow2.value = 'Credit Allocated';
    headerRow2.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: HeaderColor },
    };
    headerRow2.font = { bold: true, color: { argb: HeaderForeColor } };
    headerRow2.alignment = { horizontal: 'center', vertical: 'middle' };
    headerRow2.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };

    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: HeaderColor },
      };
      cell.font = { bold: true, color: { argb: HeaderForeColor } };
      cell.border = {
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
        top: { style: 'thin' }

      };
    });
    headers.forEach((header: any, index: number) => {
      worksheet.getColumn(index + 1).width = header.length + 25;
      worksheet.getColumn(index + 1).alignment = { horizontal: 'center' };
    });

    data.forEach((supplier: any, rowIndex: number) => {
      const rowValues = headers.map((header: any) => {
        const value = supplier[header];
        return value === undefined || value === '' ? this.defaultZeroCurrency : value;
      });
      const dataRow = worksheet.addRow(rowValues);

      const isEven = rowIndex % 2 === 0;
      const bgColor = isEven ? 'FFFFFFFF' : 'FFF9F9F9'; // light gray vs white

      // Align cells to the right for columns that contain numeric or float values
      dataRow.eachCell((cell, colNumber) => {
        const value: any = cell.value;
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
        if (this.isAmountField(value)) {
          // Stored as a number with the Accounting format: "$" pinned to the left of the cell,
          // amount right-aligned; zero shows as "$ -" (same as defaultZeroCurrency)
          const numericValue = Number(this.removeCurrencyFormatting(value));
          cell.value = isNaN(numericValue) ? 0 : numericValue;
          cell.numFmt = EXCEL_ACCOUNTING_FORMAT;
          cell.alignment = { ...EXCEL_ACCOUNTING_ALIGNMENT };
        }
        else {
          cell.alignment = { horizontal: 'left' };
        }
      });
    });


    // empty row added
    worksheet.addRow('');
    // Check if subTitleInRow is true
    // Auto-adjust column widths based on content
    worksheet.columns.forEach((column: any, index) => {
      let maxWidth = 0;
      if (index == 0) {
        column.width = InitialColumnSize
        column.alignment = { horizontal: 'left' };
      }
      else {
        column.eachCell({ includeEmpty: true }, (cell: any) => {
          // Numbers are measured as displayed ("$ 1,234.56 "), not as the raw value, so they never show as ####
          const cellTextLength = typeof cell.value === 'number'
            ? this.excelAccountingDisplayLength(cell.value)
            : cell.value ? cell.value.toString().length : 10; // Default width for empty cells
          if (cellTextLength > maxWidth) {
            maxWidth = cellTextLength;
          }
        });
        column.width = maxWidth < minimumExcelCellWidth ? minimumExcelCellWidth : maxWidth;
      }
    });

    worksheet.mergeCells('A7:A8');
    worksheet.getCell('A8').alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getCell('A8').value = headers[0];
    worksheet.getCell('A8').font = { bold: true, color: { argb: HeaderForeColor } };
    worksheet.getCell('A8').border = {
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      top: { style: 'thin' }
    };

    // Find the cell with the text 'Total' in the first column of the last row
    let totalCell: any = null;
    for (let i = worksheet.rowCount; i >= 1; i--) {
      const cell = worksheet.getCell(`A${i}`);
      if (cell && cell.value === 'Total') {
        totalCell = cell;
        break;
      }
    }

    if (totalCell) {
      const totalRowNumber = totalCell._row.number; // Using _row to get the row object and then the number
      const totalRow = worksheet.getRow(totalRowNumber);

      totalRow.eachCell((cell) => {
        cell.font = { ...EXCEL_TOTAL_ROW_FONT };

        const row = worksheet.getRow(totalRowNumber);
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          if (colNumber !== 1) {
            cell.border = {
              top: { style: 'thin' },
              bottom: { style: 'double' }
            };
          }
        });
      });
    }
  }

  // Approximate width of an amount shown with EXCEL_ACCOUNTING_FORMAT: "$", padding, separators, 2 decimals
  private excelAccountingDisplayLength(value: number): number {
    const digits = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return digits.length + (value < 0 ? 1 : 0) + 4;
  }

  // save exported data in excel file and download.
  saveExcelFile = async (buffer: any, fileName: string) => {
    const data: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });
    const url = window.URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  saveExcelBlobFile(res: any, fileName: string) {
    const url = window.URL.createObjectURL(res);
    const a = document.createElement('a');
    a.href = url;
    a.download = this.generateFilename(fileName) + '.xlsx';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }


  warningalert(msg: any, type: string): Observable<any> {
    let dialogOptions: any = {
      message: msg,
      confirmCaption: 'OK',
    };
    if (type?.toLowerCase?.() === 'warning' || type?.toLowerCase?.() === 'error') {
      dialogOptions.type = 'warning';
    }
    // Return the Observable from the dialog service
    return this.confirmDialog.successDialog(dialogOptions);
  }

  formatTime(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2); // Adding leading zero if needed
    const day = ('0' + date.getDate()).slice(-2); // Adding leading zero if needed
    const hours = ('0' + date.getHours()).slice(-2); // Adding leading zero if needed
    const minutes = ('0' + date.getMinutes()).slice(-2); // Adding leading zero if needed
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }


  // return time with 30 mins interval
  generateTimeIntervals(): string[] {
    const times: string[] = [];
    const periods = ['AM', 'PM'];
    times.push('12:00 AM');
    times.push('12:30 AM');
    for (const [index, period] of periods.entries()) {
      for (let hour = 1; hour <= 11; hour++) {
        times.push(`${hour}:00 ${period}`);
        times.push(`${hour}:30 ${period}`);
      }
      if (index + 1 < periods.length) {
        times.push('12:00 PM');
        times.push('12:30 PM');
      }
    }
    return times;
  }

  // get short forms of standard times
  getTimeZoneShortNames(value: any) {
    const words = value?.split(' ');
    const firstLetters = words?.map((word: any) =>
      (word.charAt(0) === '(') ? word.charAt(1).toUpperCase() : word.charAt(0).toUpperCase()
    )
    return firstLetters ? firstLetters?.join('') : '';
  }

  getShortName(input: string): string {
    if (!input) {
      return '';
    }
    const words = input.split(' ');
    const shortNames = [];

    for (const word of words) {
      const hasParentheses = word.startsWith('(') && word.endsWith(')');

      if (hasParentheses) {
        shortNames.push(word);
      } else {
        if (word.length > 0) {
          shortNames.push(word[0]);
        }
      }
    }
    return shortNames.join('');
  }

  displayTitleName(id: string, options: any[], key: string, key1: string) {
    const name = options?.find((item: any) => {
      return item[key1] == id
    })
    return name ? name[key] : ''
  }

  getSubstring(input: string): string {
    const trimmedInput = input.trim();
    return trimmedInput.split(' ')[0].toLowerCase();
  }


  getPlaceName = async (latitude: any, longitude: any) => {
    const apiKey = this.getLocationApiKey; // Replace with your Google Maps Geocoding API key
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`;
    try {
      const response = await fetch(url);
      const data = await response.json();
      if (data.status === 'OK' && data.results.length > 0) {
        return data.results[0]?.formatted_address;
      } else {
        return 'Unknown location';
      }
    } catch (error) {
      return 'Error fetching place name';
    }
  };

  getReadablePlatform(platform: any) {
    if (platform.includes('Win')) {
      if (platform.includes('64')) {
        return 'Windows 64-bit';
      } else {
        return 'Windows 32-bit';
      }
    }
    return platform; // Return the original platform if it's not Windows
  }

  // detail;s
  getBrowserDetails(): Observable<any> {
    const detectBrowserName = () => {
      const agent = window.navigator.userAgent.toLowerCase();
      return agent.indexOf('edge') > -1 ? 'Edge'
        : agent.indexOf('edg') > -1 ? 'Edge'
          : agent.indexOf('opr') > -1 ? 'Opera'
            : agent.indexOf('chrome') > -1 ? 'Chrome'
              : agent.indexOf('trident') > -1 ? 'Internet Explorer'
                : agent.indexOf('firefox') > -1 ? 'Firefox'
                  : agent.indexOf('safari') > -1 ? 'Safari'
                    : 'other';
    };

    const userAgent = navigator.userAgent;
    const appVersion = navigator.appVersion;
    const platform = this.getReadablePlatform(navigator.platform);
    const language = navigator.language;
    const browserName = detectBrowserName();

    const details: any = {
      userAgent,
      appVersion,
      platform,
      language,
      browserName,
    };

    return new Observable<any>((observer) => {
      if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            void (async () => {
              try {
                const { latitude, longitude } = position.coords;
                details.latitude = latitude;
                details.longitude = longitude;
                const placeName = await this.getPlaceName(latitude, longitude);
                details.placeName = placeName;
                observer.next(details);
                observer.complete();
              } catch (error) {
                observer.error(error);
              }
            })();
          },
          (error) => {
            details.error = error?.message || 'Unable to retrieve location';
            details.placeName = 'Location not available';
            observer.next(details);
            observer.complete();
          }
        );
      } else {
        details.error = 'Geolocation not supported';
        details.placeName = 'Location not available';
        observer.next(details);
        observer.complete();
      }
    });
  }

  groupBy(array: any[], key: string, sortKey?: string): any {
    return array.reduce((result, currentValue) => {
      // Get the key value from the current item
      const groupKey = currentValue[key];

      // If the group key doesn't exist in the result, create a new array
      if (!result[groupKey]) {
        result[groupKey] = [];
      }

      // Push the current value to the group
      result[groupKey].push(currentValue);

      if (sortKey) {
        result[groupKey].sort((a: any, b: any) => {
          const valueA = a[sortKey];
          const valueB = b[sortKey];

          // If both values are numbers, compare them numerically
          if (typeof valueA === 'number' && typeof valueB === 'number') {
            return valueA - valueB; // Numeric comparison (ascending order)
          }

          // If both values are strings (or any non-numeric type), compare them lexicographically
          if (typeof valueA === 'string' && typeof valueB === 'string') {
            return valueA.toUpperCase().localeCompare(valueB.toUpperCase()); // Case-insensitive string comparison
          }

          // Handle mixed types (one value is a number, the other is a string)
          if (typeof valueA === 'number') {
            return -1; // Move the number to the top (if you want the number to come first in case of mixed types)
          }

          if (typeof valueB === 'number') {
            return 1; // Move the number to the top (if you want the number to come first in case of mixed types)
          }

          return 0; // If the types are different but not number/string, leave them as they are
        })
      }


      return result;
    }, {});
  }

  nameFormat(values: string[]): string {
    return values.join('-');
  }

  maskTaxID(taxID: string): string {
    try {
      if (taxID) {
        const lastFourDigits = taxID.slice(-4);
        const maskedTaxID = 'x'.repeat(taxID.length - 4) + lastFourDigits;
        return maskedTaxID;
      } else {
        return '-';
      }
    } catch (err) {
      console.error(err)
      return '-';
    }
  }

  // get the company setting value based on key
  getCompanySettingValue(key: any) {
    let value: any = localStorage.getItem('generalSettings');
    if (value) {
      value = JSON.parse(value);
      value = value?.companySetUp?.find((item: any) => {
        return item?.settinG_NAME == key
      })
    }
    return value?.settinG_VALUE ? value.settinG_VALUE : ''
  }

  removeQueryParamsAfter(url: any, param: any) {
    const paramIndex = url.indexOf(param);
    if (paramIndex === -1) return url; // 
    return url.substring(0, paramIndex);
  }

  // toggle elements
  toggleDiv(id: any, value?: boolean) {
    const div = document.getElementById(id)
    if (div) {
      if (div.classList.contains('d-none') || value) {
        div.classList.remove('d-none');
        div.classList.add('d-block');
      } else {
        div.classList.remove('d-block');
        div.classList.add('d-none');
      }
    }
  }

  binarySearch(array: any[], target: string, searchKey: string): number {
    let left = 0;
    let right = array.length - 1;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      if (array[mid][searchKey] === target) {
        return mid;
      } else if (array[mid][searchKey] < target) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }
    return left; // Return the insertion point
  }

  // fileName format for all files
  generateFilename(supplierName: string): string {
    const date = new Date();
    const dateString = this.formatDate(date); // Use the new formatDate function
    const formattedSupplierName = supplierName.replace(/ /g, '_'); // Replace spaces with underscores
    return `${formattedSupplierName}_${dateString}`;
  }

  formatDate(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}${day}${year}`;
  }

  validateDate(date1: any, date2: any) {
    const dueDate = new Date(date1); // Example due date
    const currentDate = new Date(date2);
    if (dueDate < currentDate) {
      return false
    } else {
      return true
    }
  }
  //dynamic search 
  searchFunction(arrayCopy: any[], searchValue: string, keys: string[]): any[] {
    try {
      if (!searchValue) return arrayCopy;
      const lowerCaseSearchValue = searchValue.toLowerCase();
      return arrayCopy?.filter((item: any) =>
        keys?.some(key =>
          item[key]?.toString()?.trim()?.toLowerCase()?.includes(lowerCaseSearchValue?.trim())
        )
      );
    } catch (err) {
      console.error(err)
      return arrayCopy
    }
  }

  sortOrderList(currentSessionTime: Date, list: any) {
    const utcDate = new Date(currentSessionTime?.getTime() - (5.5 * 60 * 60 * 1000));
    for (const value of list) {
      if (new Date(value?.created_Date) <= utcDate) {
        break;
      }
      value.isCss = true;
      value.bgColor = '#eee';
    }
    return list
  }

  async getMessages(screenName: string): Promise<messages> {
    try {
      const messages: any = {};
      const res: any = await this.getMessagesApi(screenName).toPromise();
      const result = res?.result ?? [];
      result.forEach((item: any) => {
        messages[item?.messagE_CODE] = item?.messagE_TEXT;
      });
      return messages ?? {}
    } catch (error) {
      throw error;
    }
  }


  // calling message constants
  getMessagesApi(screenName: string) {
    return this._apiService
      .get(`Message/AllMessages?Module_Name=${screenName}`)
      .pipe(map((response: any) => response));
  }

  // changing values dynamically
  identifyMagnitude(value: number) {
    const absValue = Math.abs(value);
    if (absValue >= 1000000000000) {
      // value is in trillion
      return { magnitude: 'Trillions', factor: 1000000000000 };
    } else if (absValue >= 1000000000) {
      // Value is in billions
      return { magnitude: 'Billions', factor: 1000000000 };
    } else if (absValue >= 1000000) {
      // Value is in millions
      return { magnitude: 'Millions', factor: 1000000 };
    } else if (absValue >= 1000) {
      // Value is in thousands
      return { magnitude: 'Thousands', factor: 1000 };
    } else {
      // Value is less than 1000
      return { magnitude: '', factor: 1 }; // No magnitude
    }
  }

  getAmountShortFormat = (value: string): string => {
    if (value == 'Thousands') return 'K'
    else if (value == 'Millions') return 'M'
    else if (value == 'Billions') return 'B'
    return ''
  }

  // converting value dynamically
  convertMillions(value: number, maxValue: number, toFixed: number = 0) {
    const convertedMillionvalue = value / maxValue;
    return convertedMillionvalue.toFixed(toFixed);
  }

  // Check the validity of the form
  checkFormErrors(form: FormGroup) {
    if (form.invalid) {
      Object.keys(form.controls).forEach(key => {
        const control = form.get(key);
        if (control && control.invalid) {
          console.log(`${key} is invalid`);
        }
      });
      return;
    }
  }

  getAdvanceFilterArray(data: any, advanceFilterFieldsOption: FilterOption[],) {
    let filtercriteria: string = ''
    data.forEach((element: any) => {
      var advFilterField = advanceFilterFieldsOption.filter(
        (x: any) => x.display_Field_Id === element.field
      )
      if (advFilterField != null && advFilterField != undefined) {
        var advFilterValue = element.value;
        if (
          advFilterField[0].field_Type_Name == 'Multi Select' ||
          advFilterField[0].field_Type_Name == 'Dropdown List'
        ) {
          var advFilterItem = advFilterField[0]?.options?.filter((item: any) =>
            advFilterField[0]?.value?.includes(item.value)
          );
          advFilterValue = advFilterItem?.map((item: any) => item.title);
        }
        filtercriteria +=
          advFilterField[0].label_Name +
          ' ' +
          element.operator +
          ' [' +
          advFilterValue +
          ']; ';
      }
    });
    return filtercriteria
  }

  // menu actions
  getActions(): Action[] {
    return this.actionsSubject.value;
  }


  // Method to update the actions
  updateActions(newActions: Action[]) {
    this.actionsSubject.next(newActions);
  }

  getYearFinancialYears(input: string | number, noOfyears: number = 0): (string | number)[] {
    let startYear!: number;
    if (typeof input === 'number') {
      startYear = input;
    }
    else if (typeof input === 'string') {
      const startYearString = input.split('-')[0];
      startYear = parseInt(startYearString, 10);
    }
    const financialYears = []
    for (let i = 1; i <= noOfyears; i++) {
      const nextStartYear = startYear;
      const nextEndYear = nextStartYear + 1;
      if (typeof input === 'number')
        financialYears.push(nextStartYear)
      else
        financialYears.push(`${nextStartYear}-${nextEndYear.toString().slice(2)}`);

      startYear++
    }
    return financialYears;
  }


  timeDifference(DateValue: Date) {
    try {
      const now = new Date().toISOString();
      const currentDate: any = new Date(now)
      const updateDate: any = new Date(String(DateValue).replace(' ', 'T') + 'Z')
      const diffInMilliseconds = currentDate - updateDate;
      const diffInSeconds = diffInMilliseconds / 1000; // Convert to seconds
      const diffInMinutes = diffInSeconds / 60; // Convert to minutes
      const diffInHours = diffInMinutes / 60; // Convert to hours
      const diffInDays = diffInHours / 24; // Convert to days
      const diffInWeeks = diffInDays / 7; // Convert to weeks

      // Return the appropriate value based on the difference
      if (diffInMinutes < 60) {
        return `${Math.floor(diffInMinutes)} minute${Math.floor(diffInMinutes) !== 1 ? 's' : ''} ago`;
      } else if (diffInHours < 24) {
        return `${Math.floor(diffInHours)} hour${Math.floor(diffInHours) !== 1 ? 's' : ''} ago`;
      } else if (diffInDays < 7) {
        return `${Math.floor(diffInDays)} day${Math.floor(diffInDays) !== 1 ? 's' : ''} ago`;
      } else {
        return `${Math.floor(diffInWeeks)} week${Math.floor(diffInWeeks) !== 1 ? 's' : ''} ago`;
      }
    } catch (err) {
      console.error(err)
      return ''
    }
  }

  generateRange(value: string) {
    // Check if the value contains a hyphen (range format)
    if (value.includes('-')) {
      const [start, end] = value.split('-').map(Number);
      // Generate an array of numbers between start and end (inclusive)
      const rangeArray = [];
      for (let i = start; i <= end; i++) {
        rangeArray.push(i.toString());
      }
      return rangeArray;
    }

    // If it's a single value (no range), return it as an array
    return [String(value)];
  }

  parseBool(value: any) {
    if (!value)
      return false;
    else
      return (value.toString().toLowerCase() === "true");
  }

  getRangeEndDate(monthsToAdd: number, fromDate: Date): string {
    const from = new Date(fromDate);
    const to = new Date(from);
    to.setMonth(to.getMonth() + monthsToAdd);
    return this.dateCalculate(to);
  }

  dateCalculate(date: Date): string {
    date.setDate(date.getDate() - 1);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`; // Format for <input type="date">
  }

  getSimilarity(str1: string, str2: string): number {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    const longerLength = longer.length;

    if (longerLength === 0) return 1.0;

    const distance = this.levenshteinDistance(longer, shorter);
    return (longerLength - distance) / longerLength;
  }

  levenshteinDistance(a: string, b: string): number {
    const matrix = Array.from({ length: b.length + 1 }, (_, i) => [i]);
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;

    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1, // substitution
            matrix[i][j - 1] + 1,     // insertion
            matrix[i - 1][j] + 1      // deletion
          );
        }
      }
    }

    return matrix[b.length][a.length];
  }

  getDifferences(original: any[], updated: any[]): any[] {
    try {
      return updated.filter((updatedItem, index) => {
        const originalItem = original[index];
        return JSON.stringify(updatedItem) !== JSON.stringify(originalItem);
      });
    } catch (error) {
      console.error(error)
      return []
    }
  };

  // get companies of user for switching
  getMergeCompanies(user_id: number) {
    return this._apiService
      .get(`MergeCompany/GetCompaniesForSwitchAsync?user_Id=${user_id}`)
      .pipe(map((response: any) => response));
  }

  checkEmptyValues(
    data: any,
    keys: string[]
  ): boolean {
    try {
      if (!data) return true;

      for (const item of data) {
        for (const key of keys) {
          const value = item[key];
          if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) {
            return false;
          }
        }
      }

      return true;
    } catch (err) {
      console.error(err)
      return true;
    }
  }

  arrayCompare(obj1: any[], obj2: any[]) {
    try {
      if (typeof obj1 !== typeof obj2) return false;
      if (typeof obj1 !== 'object' || obj1 === null || obj2 === null) {
        return obj1 === obj2;
      }
      const keys1: any = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      if (keys1.length !== keys2.length) return false;

      return keys1.every((key: any) => this.arrayCompare(obj1[key], obj2[key]));
    } catch (err) {
      console.error(err)
      return ''
    }
  }

  // getCompany Categories
  getCompanyAgencyCategories(coId: string | null): void {
    try {
      const storedCategories = sessionStorage.getItem('companyCategories');
      if (storedCategories) {
        const parsed = JSON.parse(storedCategories);
        this.agencyCategoryOptions.next(parsed);
      } else {
        const localCompanyId = localStorage.getItem('companyId');
        const companyId = localCompanyId && JSON.parse(localCompanyId) !== null ? localCompanyId : coId;
        companyId && this._apiService.get(`Categories/GetCertificateCategories?Co_id=${companyId}`).subscribe({
          next: (response: any) => {
            const categories = response['result'].map((item: { id: number; category_Name: string; priority_Order: number }) => ({
              value: item.id,
              name: item.category_Name
            }));
            sessionStorage.setItem('companyCategories', JSON.stringify(categories));
            this.agencyCategoryOptions.next(categories);
          }
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  getAllIndexes(fromText: string, ofText: string): number[] {
    const indexes = [];
    let loopIndex = 0;

    while (true) {
      let currentIndex = fromText.indexOf(ofText, loopIndex); // Start searching from index 0

      if (currentIndex == -1)
        break;

      indexes.push(currentIndex);
      loopIndex = currentIndex + 1;
    }

    return indexes;
  }

  zipAndDownloadFiles(array: Blob[], name: string = 'default', names?: string[]) {
    try {
      const zip = new JSZip();

      array.forEach((file: Blob, index: number) => {
        const fileName = names?.[index] ? `${names[index]}.xlsx` : `file${index + 1}.xlsx`;
        zip.file(fileName, file); // Add file to zip
      });

      zip.generateAsync({ type: 'blob' })
        .then((content: Blob) => {
          saveAs(content, `${name}.zip`);
        })
        .catch(err => {
          console.error('Zip generation failed', err);
        });
    } catch (err) {
      console.error('Error creating zip:', err);
    }
  }

  handleDateFieldEvents(event1: FocusEvent, event1Source?: any) {
    try {
      const event1Target = event1.target as HTMLInputElement;

      /** Add Place Holder text to Date field **/
      event1Target.placeholder = defaultValue.dateInputFormat;
      event1Target.maxLength = 10;
      var dateFormat = event1Target.placeholder;

      if (!dateFormat)
        dateFormat = "MM/DD/YYYY";
      else
        dateFormat = dateFormat.toUpperCase();

      if (dateFormat.startsWith("YYYY")) {
        var seperator1Index = 4;
        var seperator2Index = 7;
      }
      else {
        var seperator1Index = 2;
        var seperator2Index = 5;
      }

      var seperator = dateFormat.charAt(seperator1Index);

      /** Add input event to Date field **/
      event1Target.addEventListener('input',
        (event4: Event) => {
          const event4Target = event4.target as HTMLInputElement;
          let input = event4Target.value;

          if (!input.endsWith(seperator))
            input = input.replaceAll(" ", seperator);

          if (input.endsWith(seperator + seperator))
            input = input.replaceAll(seperator + seperator, seperator);

          event4Target.value = input;
          let seperatorCount = input.count(seperator);
          let seperator1Added = seperatorCount > 0;
          let seperator2Added = seperatorCount > 1;

          if (!seperator1Added && input.length == seperator1Index) {
            input = input + seperator;
            seperator1Added = true;
          }
          else if (!seperator2Added && input.length == seperator2Index) {
            input = input + seperator;
            seperator2Added = true;
          }

          event4Target.value = input;
        });

      /** Add blur event to Date field **/
      event1Target.addEventListener('blur',
        (event2: FocusEvent) => {
          const event2Target = event2.target as HTMLInputElement;

          /** Remove Place Holder text on blur **/
          event2Target.placeholder = "";
          event2Target.value = event2Target.value.trim();

          if (event1Source && event1Source.setValue !== undefined) {
            event1Source.setValue(new Date(event2Target.value));
          }
        });

      /** Add Key Press Event to Date field **/
      event1Target.addEventListener('keypress',
        (event3: KeyboardEvent) => {
          var charAccepted = ("0123456789 -/.".indexOf(event3.key) > -1);

          if (charAccepted)
            return true;

          event3.preventDefault();
          return false;
        });
    } catch (err) {
      console.error(err)
    }
  }

  handleDateTimeFieldEvents(event1: FocusEvent) {
    try {
      const event1Target = event1.target as HTMLInputElement;

      if (!event1Target)
        return;

      /** Add Place Holder text to Date field **/
      event1Target.placeholder = defaultValue.dateTimeInputFormat;
      event1Target.maxLength = 20;
      var seperators = ["/", "/", ", ", ":", " "];
      var seperatorIndexes = [2, 5, 10, 14, 17];

      /** Add input event to Date field **/
      event1Target.addEventListener('input',
        (event4: Event) => {
          //var input = event4.target.value;
          const event4Target = event4.target as HTMLInputElement;
          var input = event4Target.value;

          if (input.length == 20 || input.endsWith(" ") || input.endsWith("/") || input.endsWith(",") || input.endsWith(":")) {
            var dateTimeParts = [];
            input = input.replaceAll("/", " ").replace(",", " ").replace("  ", " ").replace(":", " ");
            let dateParts = input.split(" ");

            for (let i = 0; i < dateParts.length; i++) {
              if (!dateParts[i])
                continue;

              let partValue = dateParts[i];
              dateTimeParts[i] = (partValue.length >= 2) ? partValue : "0" + partValue;
            }

            var result = "";

            for (let i = 0; i < dateTimeParts.length; i++) {
              result = result + dateTimeParts[i] + (seperators[i] || "");
            }

            event4Target.value = result.toUpperCase();
          }
          else {
            var seperatorIndex = seperatorIndexes.indexOf(input.length);

            if (seperatorIndex == -1)
              return;

            var seperator = seperators[seperatorIndex];

            if (input.endsWith(seperator))
              return;

            event4Target.value = input + seperator;
          }
        });

      /** Add blur event to Date field **/
      event1Target.addEventListener('blur',
        (event2: FocusEvent) => {
          const event2Target = event2.target as HTMLInputElement;

          /** Remove Place Holder text on blur **/
          event2Target.placeholder = "";
          event2Target.value = event2Target.value.trim();
        });

      /** Add Key Press Event to Date field **/
      event1Target.addEventListener('keypress',
        (event3: KeyboardEvent) => {
          var charAccepted = ("0123456789, -/:.APMapm".indexOf(event3.key) > -1);

          if (charAccepted)
            return true;

          event3.preventDefault();
          return false;
        });
    } catch (err) {
      console.error(err)
    }
  }

  handleHttpError<T>(allbackValue: T) {
    return catchError((error: any) => {
      console.error('HTTP Error:', error);
      this.warningalert(defaultValue?.apiErrorMsg, 'warning');
      return of(allbackValue);
    });
  }


  //download blob file from url
  downloadFile(url: string) {
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = url.split('/').pop() || 'download'; // optional: set a default filename
      link.target = '_blank'; // so it opens in a new tab if download attr is ignored
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error(err)
    }
  }

  //Get Report Wise Type
  getReportWiseType() {
    try {
      const storedData: any = localStorage?.getItem('filteredReportingList');
      const reportData = JSON.parse(storedData); let isAggregate = false
      if (reportData?.length > 0) {
        isAggregate = reportData[0]?.is_Aggregate
      }
      return isAggregate
    } catch (err) {
      console.error(err)
      return false
    }
  }

}
